import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Global font size store with localStorage persistence
 *
 * Manages user's preferred font size for accessibility.
 * Uses Zustand's persist middleware to save preference to localStorage
 * and restore on app reload.
 *
 * Font Size Range: 12px (minimum) to 24px (maximum)
 * Default: 16px (standard)
 * Increment/Decrement: 2px per action
 *
 * Storage Key: "fontSize" (persisted in localStorage)
 *
 * @returns {Object} Font size store with state and actions
 */
const useFontSizeStore = create(
  persist(
    (set) => ({
      /**
       * Current font size in pixels
       * Range: 12-24 (12px minimum, 24px maximum)
       * @type {number}
       */
      fontSize: 16,

      /**
       * Increase font size by 2 pixels
       *
       * Clamped to maximum of 24px
       */
      increaseFont: () =>
        set((state) => ({
          fontSize: Math.min(state.fontSize + 2, 24),
        })),

      /**
       * Decrease font size by 2 pixels
       *
       * Clamped to minimum of 12px
       */
      decreaseFont: () =>
        set((state) => ({
          fontSize: Math.max(state.fontSize - 2, 12),
        })),

      /**
       * Reset font size to default (16px)
       */
      resetFont: () => set({ fontSize: 16 }),
    }),
    {
      // Storage key for localStorage
      name: "fontSize", 
    }
  )
);

/**
 * Global Zustand font size store with localStorage persistence
 * 
 * Usage:
 *   import useFontSizeStore from './fontSizeStore'
 *   const { fontSize, increaseFont, decreaseFont, resetFont } = useFontSizeStore()
 */
export default useFontSizeStore;
